import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { EmojiPicker } from '@hivebrite/design-system/EmojiPicker';
import { zIndices } from '@hivebrite/design-system/Theme';

import { useQuillEditorContext } from 'hb-react/shared/components/DesignSystem/QuillEditor/contexts/QuillEditorContext';
import useInputAndDropdownTheme from 'hb-react/shared/hooks/theme/useInputAndDropdownTheme';
import ThemeProvider from 'hb-react/style/ThemeProvider';

const MAX_EMOJI_VERSION = '10.0';

const EmojiPickerFlyout = () => {
  const {
    onEmojiClick,
    quillRef,
    toggleEmojiPicker,
    toolbarRef,
    editorWrapperRef,
  } = useQuillEditorContext();
  const inputAndDropdownTheme = useInputAndDropdownTheme();

  const [emojiPickerPosition, setEmojiPickerPosition] = useState();

  const emojiPickerRef = useRef();

  const isOpenInModal =
    !!document.querySelector('dialog') ||
    !!document.querySelector('[data-id="design-system-modal"]');

  // Separation with the Emoji Picker
  const additionalSpace = 12;

  const handleClickOutside = (event) => {
    // Finding if the event occurs from the emoji picker icon (toolbar).
    let isEmojiPickerIcon = false;
    const lastChild = event.target?.parentElement?.parentElement?.lastChild;

    // nodeType 1 makes sure it's an element node (and that it has a getAttribute method)
    if (lastChild?.nodeType === 1) {
      isEmojiPickerIcon = lastChild.getAttribute('id') === 'emoji-picker-icon';
    }

    if (
      emojiPickerRef.current &&
      !emojiPickerRef.current.contains(event.target) &&
      !isEmojiPickerIcon
    ) {
      toggleEmojiPicker();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      toggleEmojiPicker();
    }
  };

  const getPositionInModal = useCallback(
    ({
      widthEmojiPicker,
      heightEmojiPicker,

      heightQuill,

      yQuill,
      heightToolbar,
      yToolbar,
      leftEmojiIconInToolbar,
    }) => {
      let position;
      const leftPosition = leftEmojiIconInToolbar + 8 - widthEmojiPicker / 2;

      // Verify if the bottom side has available space to display the Emoji Picker
      if (
        yToolbar + heightToolbar + additionalSpace + heightEmojiPicker <
        window.innerHeight
      ) {
        position = {
          top: `${yToolbar + heightToolbar + additionalSpace}px`,
          left: `${leftPosition}px`,
        };
      }

      // Verify if the top side has available space to display the Emoji Picker
      if (
        !position &&
        yQuill + heightQuill - additionalSpace > heightEmojiPicker
      ) {
        position = {
          top: `${yToolbar - (additionalSpace + heightEmojiPicker)}px`,
          left: `${leftPosition}px`,
        };
      }

      // We display the Emoji Picker below the Quill Editor if there is not available space
      position = position ?? {
        top: `${yToolbar + heightToolbar + additionalSpace}px`,
        left: `${leftPosition}px`,
      };

      return position;
    },
    [],
  );

  const getPositionInPage = useCallback(
    ({
      widthEmojiPicker,
      heightEmojiPicker,
      heightQuill,
      heightToolbar,
      yQuill,
      leftToolbar,
      topToolbar,
      leftEmojiIconInToolbar,
    }) => {
      let position;
      const leftPosition =
        leftEmojiIconInToolbar - leftToolbar + 8 - widthEmojiPicker / 2;

      // Verify if the bottom side has available space to display the Emoji Picker
      if (
        topToolbar + heightToolbar + additionalSpace + heightEmojiPicker <
        window.innerHeight
      ) {
        position = {
          top: `${heightQuill + heightToolbar + additionalSpace}px`,
          left: `${leftPosition}px`,
        };
      }

      // Verify if the top side has available space to display the Emoji Picker
      if (
        !position &&
        yQuill + heightQuill - additionalSpace > heightEmojiPicker
      ) {
        position = {
          top: `${heightQuill - additionalSpace - heightEmojiPicker}px`,
          left: `${leftPosition}px`,
        };
      }

      // We display the Emoji Picker below the Quill Editor if there is not available space
      position = position ?? {
        top: `${heightQuill + heightToolbar + additionalSpace}px`,
        left: `${leftPosition}px`,
      };

      return position;
    },
    [],
  );

  useEffect(() => {
    // When the Emoji Picker is displayed
    if (emojiPickerRef.current) {
      // We get width and height of the Emoji Picker
      const { width: widthEmojiPicker, height: heightEmojiPicker } =
        emojiPickerRef.current.getBoundingClientRect();

      const { y: yQuill } = quillRef.current.container.getBoundingClientRect();

      const {
        height: heightToolbar,
        y: yToolbar,
        left: leftToolbar,
        top: topToolbar,
      } = toolbarRef.current.getBoundingClientRect();

      // We need the height of the entire editor wrapper to calculate the editor content height including the embedly preview
      const { height: editorWrapperHeight } =
        editorWrapperRef.current.getBoundingClientRect();
      const heightQuill = editorWrapperHeight - heightToolbar;

      const { left: leftEmojiIconInToolbar } = toolbarRef.current
        .querySelector('[data-testid="emoji-picker-icon"]')
        .getBoundingClientRect();

      let position;

      if (isOpenInModal) {
        position = getPositionInModal({
          widthEmojiPicker,
          heightEmojiPicker,
          heightQuill,
          yQuill,
          heightToolbar,
          yToolbar,
          leftEmojiIconInToolbar,
        });
      } else {
        position = getPositionInPage({
          widthEmojiPicker,
          heightEmojiPicker,
          heightQuill,
          yQuill,
          heightToolbar,
          leftToolbar,
          topToolbar,
          leftEmojiIconInToolbar,
        });
      }

      setEmojiPickerPosition(position);
    }
  }, [
    isOpenInModal,
    getPositionInModal,
    getPositionInPage,
    quillRef,
    toolbarRef,
    editorWrapperRef,
  ]);

  useEffect(() => {
    if (emojiPickerRef.current) {
      document.addEventListener('click', handleClickOutside, true);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  });

  return (
    // Emoji picker is not taking input theming for now - stay with default black fontBrightness
    <ThemeProvider theme={inputAndDropdownTheme.themeName}>
      <Wrapper
        $isOpenInModal={isOpenInModal}
        $position={emojiPickerPosition}
        ref={emojiPickerRef}
        data-testid="emoji-picker"
        onKeyDown={handleKeyDown}
      >
        <EmojiPicker
          maxVersion={MAX_EMOJI_VERSION}
          onEmojiPick={onEmojiClick}
        />
      </Wrapper>
    </ThemeProvider>
  );
};

export default EmojiPickerFlyout;

const Wrapper = styled(Box)`
  position: ${({ $isOpenInModal }) => ($isOpenInModal ? 'fixed' : 'absolute')};
  z-index: ${zIndices.popover};
  left: ${({ $position }) => $position?.left ?? 'auto'};
  top: ${({ $position }) => $position?.top ?? 'auto'};
  visibility: ${({ $position }) => ($position ? 'visible' : 'hidden')};
`;
